import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/AuthContext'
import { format, differenceInMinutes } from 'date-fns'
import toast from 'react-hot-toast'
import { Clock, LogIn, LogOut, CheckCircle } from 'lucide-react'

interface ShiftRow {
  id:           string
  shift_date:   string
  clock_in:     string | null
  clock_out:    string | null
  hours_worked: number | null
  notes:        string | null
}

interface ShiftClockWidgetProps {
  compact?: boolean
}

export default function ShiftClockWidget({ compact = false }: ShiftClockWidgetProps) {
  const { agent } = useAuth()
  const [todayShift,  setTodayShift]  = useState<ShiftRow | null>(null)
  const [isLoading,   setIsLoading]   = useState(true)
  const [isActioning, setIsActioning] = useState(false)
  const [elapsed,     setElapsed]     = useState(0) // seconds

  const today = new Date().toISOString().slice(0, 10)

  const loadShift = useCallback(async () => {
    if (!agent) return
    setIsLoading(true)
    const { data } = await supabase
      .from('shifts')
      .select('*')
      .eq('agent_id', agent.id)
      .eq('shift_date', today)
      .maybeSingle()

    setTodayShift(data as ShiftRow | null)
    setIsLoading(false)
  }, [agent, today])

  useEffect(() => { void loadShift() }, [loadShift])

  // Live elapsed timer while clocked in
  useEffect(() => {
    if (!todayShift?.clock_in || todayShift?.clock_out) { setElapsed(0); return }
    const tick = () => setElapsed(differenceInMinutes(new Date(), new Date(todayShift.clock_in!)) * 60)
    tick()
    const id = setInterval(tick, 30_000)
    return () => clearInterval(id)
  }, [todayShift])

  async function clockIn() {
    if (!agent) return
    setIsActioning(true)
    const now = new Date().toISOString()

    // Upsert today's shift row — hours_worked is GENERATED ALWAYS, do not set it
    const { error } = await supabase
      .from('shifts')
      .upsert({
        agent_id:   agent.id,
        shift_date: today,
        clock_in:   now,
        clock_out:  null,
      }, { onConflict: 'agent_id,shift_date' })

    if (error) {
      toast.error(error.message)
    } else {
      toast.success(`Clocked in at ${format(new Date(now), 'h:mma')}`)
      void loadShift()
    }
    setIsActioning(false)
  }

  async function clockOut() {
    if (!agent || !todayShift?.id) return
    setIsActioning(true)
    const now   = new Date().toISOString()
    const start = todayShift.clock_in ? new Date(todayShift.clock_in) : new Date()
    // hours_worked is GENERATED ALWAYS in DB — only set clock_out, Postgres handles the rest
    const hoursWorked = differenceInMinutes(new Date(now), start) / 60

    const { error } = await supabase
      .from('shifts')
      .update({ clock_out: now })
      .eq('id', todayShift.id)

    if (error) {
      toast.error(error.message)
    } else {
      toast.success(`Clocked out — ${hoursWorked.toFixed(1)}h logged`)
      void loadShift()
    }
    setIsActioning(false)
  }

  // ── Display helpers ────────────────────────────────────────
  const isClockedIn  = !!todayShift?.clock_in && !todayShift?.clock_out
  const isCompleted  = !!todayShift?.clock_in && !!todayShift?.clock_out

  const elapsedHours = Math.floor(elapsed / 3600)
  const elapsedMins  = Math.floor((elapsed % 3600) / 60)
  const elapsedLabel = elapsed > 0 ? `${elapsedHours}h ${elapsedMins}m` : '—'

  // ── Compact mode (sidebar icon) ───────────────────────────
  if (compact) {
    return (
      <button
        id="sidebar-clock-btn"
        title={isClockedIn ? `On shift · ${elapsedLabel}` : isCompleted ? 'Shift complete' : 'Clock in'}
        onClick={() => { if (!isClockedIn && !isCompleted) void clockIn(); else if (isClockedIn) void clockOut() }}
        disabled={isActioning || isLoading}
        style={{
          width: '2rem', height: '2rem',
          borderRadius: '50%',
          background: isClockedIn ? 'rgba(16,185,129,0.15)' : 'var(--bg-surface)',
          border: `1px solid ${isClockedIn ? 'rgba(16,185,129,0.3)' : 'var(--border-subtle)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: isCompleted ? 'default' : 'pointer',
          transition: 'all 200ms ease',
          boxShadow: isClockedIn ? '0 0 8px rgba(16,185,129,0.25)' : 'none',
        }}
        aria-label={isClockedIn ? 'Clock out' : 'Clock in'}
      >
        <Clock size={13} style={{ color: isClockedIn ? 'var(--status-success)' : 'var(--text-muted)' }} />
      </button>
    )
  }

  return (
    <div
      className="card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.875rem 1.25rem',
        background: isClockedIn
          ? 'rgba(16,185,129,0.06)'
          : 'var(--bg-elevated)',
        borderColor: isClockedIn ? 'rgba(16,185,129,0.2)' : 'var(--border-subtle)',
        transition: 'all 300ms ease',
      }}
    >
      {/* Clock icon */}
      <div style={{
        width: '2.5rem', height: '2.5rem',
        borderRadius: '50%',
        background: isClockedIn ? 'rgba(16,185,129,0.15)' : 'var(--bg-surface)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Clock size={16} style={{ color: isClockedIn ? 'var(--status-success)' : 'var(--text-muted)' }} />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.125rem' }}>
          {isCompleted ? 'Shift Complete' : isClockedIn ? 'On Shift' : 'Not Clocked In'}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          {isLoading
            ? '…'
            : isCompleted
            ? `${format(new Date(todayShift!.clock_in!), 'h:mma')} → ${format(new Date(todayShift!.clock_out!), 'h:mma')} · ${todayShift!.hours_worked?.toFixed(1)}h`
            : isClockedIn
            ? `Since ${format(new Date(todayShift!.clock_in!), 'h:mma')} · ${elapsedLabel}`
            : format(new Date(), 'EEEE, MMM d')
          }
        </div>
      </div>

      {/* Action button */}
      {!isLoading && (
        isCompleted ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: 'var(--status-success)', fontSize: '0.8125rem', fontWeight: 600 }}>
            <CheckCircle size={14} /> Done
          </div>
        ) : isClockedIn ? (
          <button
            className="btn btn-danger btn-sm"
            id="clock-out-btn"
            onClick={() => void clockOut()}
            disabled={isActioning}
          >
            <LogOut size={13} />
            {isActioning ? '…' : 'Clock Out'}
          </button>
        ) : (
          <button
            className="btn btn-primary btn-sm"
            id="clock-in-btn"
            onClick={() => void clockIn()}
            disabled={isActioning}
          >
            <LogIn size={13} />
            {isActioning ? '…' : 'Clock In'}
          </button>
        )
      )}
    </div>
  )
}
